import { criarErroImpressao } from './PrintOperation';

type SocketCreateInfo = {
    socketId?: number;
};

type SocketSendInfo = {
    resultCode?: number;
    bytesSent?: number;
};

export interface TcpSocketApi {
    create: (properties: Record<string, never>, callback: (createInfo: SocketCreateInfo) => void) => void;
    connect: (socketId: number, ip: string, porta: number, callback: (result: number) => void) => void;
    send: (socketId: number, data: ArrayBuffer, callback: (sendInfo: SocketSendInfo) => void) => void;
    close: (socketId: number) => void;
}

interface EnviarBufferTcpOptions {
    tcp: TcpSocketApi | undefined;
    ip: string;
    porta: number;
    data: ArrayBuffer;
    timeoutMs?: number;
    aoIniciarEnvio?: () => void;
}

/**
 * Abre uma conexão TCP, envia todo o buffer e sempre encerra o socket.
 * O timeout cobre create, connect e send para impedir que a fila fique travada.
 */
export function enviarBufferTcp({
    tcp,
    ip,
    porta,
    data,
    timeoutMs = 10000,
    aoIniciarEnvio,
}: EnviarBufferTcpOptions): Promise<void> {
    if (!tcp) {
        return Promise.reject(new Error('API de impressão em rede indisponível'));
    }

    return new Promise<void>((resolve, reject) => {
        let finalizado = false;
        let socketId: number | null = null;
        let etapa: 'create' | 'connect' | 'send' = 'create';
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const fecharSocket = () => {
            if (socketId === null) return;

            try {
                tcp.close(socketId);
            } catch {
                // O socket já pode ter sido fechado pelo plugin.
            }

            socketId = null;
        };

        const finalizar = (error?: Error) => {
            if (finalizado) return;
            finalizado = true;
            if (timeoutId) clearTimeout(timeoutId);
            fecharSocket();

            if (error) {
                reject(error);
            } else {
                resolve();
            }
        };

        const iniciarTimeoutDaEtapa = () => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const envioIniciado = etapa === 'send';
                finalizar(criarErroImpressao(`Timeout Rede na etapa ${etapa} após ${timeoutMs}ms`, {
                    retryable: !envioIniciado,
                    deliveryUncertain: envioIniciado,
                }));
            }, timeoutMs);
        };

        iniciarTimeoutDaEtapa();

        try {
            tcp.create({}, createInfo => {
                if (finalizado) {
                    if (typeof createInfo?.socketId === 'number') {
                        try {
                            tcp.close(createInfo.socketId);
                        } catch {
                            // O timeout já encerrou esta tentativa.
                        }
                    }
                    return;
                }

                if (typeof createInfo?.socketId !== 'number') {
                    finalizar(new Error('Falha ao criar socket de impressão'));
                    return;
                }

                socketId = createInfo.socketId;
                etapa = 'connect';
                iniciarTimeoutDaEtapa();

                try {
                    tcp.connect(socketId, ip, porta, result => {
                        if (finalizado) return;

                        if (result !== 0) {
                            finalizar(new Error(`Falha conexão Rede (${result})`));
                            return;
                        }

                        try {
                            etapa = 'send';
                            iniciarTimeoutDaEtapa();
                            aoIniciarEnvio?.();
                            tcp.send(socketId as number, data, sendInfo => {
                                if (finalizado) return;

                                const resultCode = sendInfo?.resultCode;
                                const bytesEnviados = typeof sendInfo?.bytesSent === 'number'
                                    ? sendInfo.bytesSent
                                    : (typeof resultCode === 'number' && resultCode >= 0 ? resultCode : undefined);

                                if (typeof resultCode === 'number' && resultCode < 0) {
                                    finalizar(criarErroImpressao(`Falha envio Rede (${resultCode})`, {
                                        retryable: false,
                                        deliveryUncertain: true,
                                    }));
                                    return;
                                }

                                if (typeof bytesEnviados !== 'number' || bytesEnviados !== data.byteLength) {
                                    finalizar(criarErroImpressao(`Envio parcial Rede (${bytesEnviados ?? 0}/${data.byteLength} bytes)`, {
                                        retryable: false,
                                        deliveryUncertain: true,
                                    }));
                                    return;
                                }

                                finalizar();
                            });
                        } catch (error) {
                            finalizar(error instanceof Error ? error : new Error('Falha envio Rede'));
                        }
                    });
                } catch (error) {
                    finalizar(error instanceof Error ? error : new Error('Falha conexão Rede'));
                }
            });
        } catch (error) {
            finalizar(error instanceof Error ? error : new Error('Falha ao criar socket de impressão'));
        }
    });
}
